import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { thumbnailAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Image,
  Loader2,
  Sparkles,
  Download,
  RefreshCw,
  Palette,
  Type,
  Wand2,
  History,
  Trash2,
  Eye,
  Clock,
  ChevronRight,
} from "lucide-react";
import MediaPickerModal, { MediaPickerButton } from "../components/MediaPickerModal";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const styles = [
  { value: "vibrant", label: "Vibrant", desc: "Bold colors, high energy, neon accents" },
  { value: "minimal", label: "Minimal", desc: "Clean, simple, elegant design" },
  { value: "dramatic", label: "Dramatic", desc: "Cinematic, moody, intense lighting" },
  { value: "aesthetic", label: "Aesthetic", desc: "Soft pastels, dreamy, warm tones" },
];

const styleColors = {
  vibrant: "bg-[#FF0055]/20 text-[#FF0055]",
  minimal: "bg-[#00F0FF]/20 text-[#00F0FF]",
  dramatic: "bg-[#A855F7]/20 text-[#A855F7]",
  aesthetic: "bg-[#F59E0B]/20 text-[#F59E0B]",
};

export default function ThumbnailGeneratorPage() {
  const [title, setTitle] = useState("");
  const [script, setScript] = useState("");
  const [style, setStyle] = useState("vibrant");
  const [includeText, setIncludeText] = useState(false);  // Default OFF
  const [loading, setLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  
  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [viewingThumbnail, setViewingThumbnail] = useState(null);
  const [loadingThumbnailId, setLoadingThumbnailId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [thumbnailToDelete, setThumbnailToDelete] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Fetch history on mount
  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const response = await thumbnailAPI.getHistory(20, 0);
      setHistory(response.data.thumbnails || []);
    } catch (error) {
      console.error("Failed to fetch thumbnail history:", error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleGenerate = async () => {
    if (!title || !script) {
      toast.error("Please enter a title and script");
      return;
    }

    setLoading(true);
    try {
      const response = await thumbnailAPI.generateThumbnail({
        title,
        script,
        style,
        include_text: includeText,
      });
      setGeneratedImage(response.data);
      toast.success("Thumbnail generated and saved to history!");
      // Refresh history to include new thumbnail
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate thumbnail");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (imageData, thumbnailTitle) => {
    if (!imageData) return;
    
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${imageData}`;
    link.download = `thumbnail-${(thumbnailTitle || title).replace(/\s+/g, "-").toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Thumbnail downloaded!");
  };

  const handleRegenerate = () => {
    setGeneratedImage(null);
    handleGenerate();
  };

  const handleViewThumbnail = async (thumbnailId) => {
    setLoadingThumbnailId(thumbnailId);
    try {
      const response = await thumbnailAPI.getById(thumbnailId);
      setViewingThumbnail(response.data);
    } catch (error) {
      toast.error("Failed to load thumbnail");
    } finally {
      setLoadingThumbnailId(null);
    }
  };

  const handleDeleteClick = (thumbnail) => {
    setThumbnailToDelete(thumbnail);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!thumbnailToDelete) return;
    
    try {
      await thumbnailAPI.delete(thumbnailToDelete.id);
      toast.success("Thumbnail deleted");
      setHistory(history.filter(t => t.id !== thumbnailToDelete.id));
      if (viewingThumbnail?.id === thumbnailToDelete.id) {
        setViewingThumbnail(null);
      }
    } catch (error) {
      toast.error("Failed to delete thumbnail");
    } finally {
      setDeleteDialogOpen(false);
      setThumbnailToDelete(null);
    }
  };

  const handleUseThumbnail = (thumbnail) => {
    // Load the full thumbnail data and set it as the current generated image
    setViewingThumbnail(null);
    setGeneratedImage({
      image_data: thumbnail.image_data,
      prompt_used: thumbnail.prompt_used,
      style: thumbnail.style,
      id: thumbnail.id
    });
    setTitle(thumbnail.title);
    setScript(thumbnail.script);
    setStyle(thumbnail.style);
    setIncludeText(thumbnail.include_text);
    setShowHistory(false);
    toast.success("Thumbnail loaded from history");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-7xl mx-auto space-y-8"
      data-testid="thumbnail-generator-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Image className="w-8 h-8 text-[#00F0FF]" />
            AI Thumbnail Generator
          </h1>
          <p className="text-muted-foreground mt-2">
            Create eye-catching thumbnails for your TikTok videos using AI.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowHistory(!showHistory)}
          className={`gap-2 ${showHistory ? 'bg-[#00F0FF]/10 border-[#00F0FF]' : 'border-border/50'}`}
          data-testid="toggle-history-btn"
        >
          <History className="w-4 h-4" />
          History ({history.length})
        </Button>
      </div>

      {/* History Panel */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="bg-card border-border/50">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-[#00F0FF]" />
                  Thumbnail History
                </CardTitle>
                <CardDescription>
                  Your previously generated thumbnails are automatically saved here
                </CardDescription>
              </CardHeader>
              <CardContent>
                {historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No thumbnails saved yet</p>
                    <p className="text-sm">Generate your first thumbnail to start building your library</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {history.map((thumbnail) => (
                      <motion.div
                        key={thumbnail.id}
                        variants={itemVariants}
                        className="group relative bg-secondary/30 rounded-lg border border-border/30 overflow-hidden hover:border-[#00F0FF]/50 transition-all"
                      >
                        <div 
                          className="aspect-[9/16] bg-secondary/50 flex items-center justify-center cursor-pointer overflow-hidden"
                          onClick={() => handleViewThumbnail(thumbnail.id)}
                        >
                          {loadingThumbnailId === thumbnail.id ? (
                            <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
                          ) : thumbnail.image_data ? (
                            <img 
                              src={`data:image/png;base64,${thumbnail.image_data}`}
                              alt={thumbnail.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-center p-2">
                              <Image className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                              <p className="text-xs text-muted-foreground">Click to view</p>
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-xs font-medium text-white truncate" title={thumbnail.title}>
                            {thumbnail.title}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <Badge className={`text-[10px] px-1.5 py-0 ${styleColors[thumbnail.style] || styleColors.vibrant}`}>
                              {thumbnail.style}
                            </Badge>
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(thumbnail.created_at).split(',')[0]}
                            </span>
                          </div>
                        </div>
                        {/* Hover Actions */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            className="h-8 w-8 p-0"
                            onClick={() => handleViewThumbnail(thumbnail.id)}
                            data-testid={`view-thumbnail-${thumbnail.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-8 w-8 p-0"
                            onClick={() => handleDeleteClick(thumbnail)}
                            data-testid={`delete-thumbnail-${thumbnail.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Thumbnail Modal */}
      <AnimatePresence>
        {viewingThumbnail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setViewingThumbnail(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border/50 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-border/30">
                <h3 className="font-semibold text-white truncate">{viewingThumbnail.title}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={styleColors[viewingThumbnail.style] || styleColors.vibrant}>
                    {viewingThumbnail.style}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(viewingThumbnail.created_at)}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <div className="aspect-[9/16] max-h-[50vh] bg-secondary/30 rounded-lg overflow-hidden">
                  <img
                    src={`data:image/png;base64,${viewingThumbnail.image_data}`}
                    alt={viewingThumbnail.title}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="p-4 border-t border-border/30 flex gap-3">
                <Button
                  onClick={() => handleDownload(viewingThumbnail.image_data, viewingThumbnail.title)}
                  className="flex-1 bg-[#00FF94] hover:bg-[#00FF94]/90 text-[#0A0A0B] font-bold rounded-xl gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
                <Button
                  onClick={() => handleUseThumbnail(viewingThumbnail)}
                  className="flex-1 bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-bold rounded-xl gap-2"
                >
                  <ChevronRight className="w-4 h-4" />
                  Use This
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setViewingThumbnail(null)}
                  className="border-border/50"
                >
                  Close
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Thumbnail?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{thumbnailToDelete?.title}" from your history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <motion.div variants={itemVariants} className="space-y-6">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-[#FF0055]" />
                Thumbnail Settings
              </CardTitle>
              <CardDescription>
                Describe your video and let AI create the perfect thumbnail
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label className="text-white">Video Title</Label>
                <Input
                  placeholder="e.g., 'The Morning Routine That Changed My Life'"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="h-12 bg-secondary/50 border-border/50"
                  data-testid="thumbnail-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Script / Content Description</Label>
                <Textarea
                  placeholder="Describe your video content or paste your full script here. The more detail you provide, the better the thumbnail will match your content..."
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  className="min-h-[200px] bg-secondary/50 border-border/50 resize-y"
                  maxLength={5000}
                  data-testid="thumbnail-script-input"
                />
                <p className="text-xs text-muted-foreground text-right">{script.length}/5000</p>
              </div>

              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Visual Style
                </Label>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger className="h-12 bg-secondary/50 border-border/50" data-testid="thumbnail-style-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {styles.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        <div className="flex flex-col">
                          <span>{s.label}</span>
                          <span className="text-xs text-muted-foreground">{s.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl">
                <div className="flex items-center gap-3">
                  <Type className="w-5 h-5 text-[#00F0FF]" />
                  <div>
                    <p className="text-white font-medium">Include Text Overlay</p>
                    <p className="text-xs text-muted-foreground">Add title text to thumbnail</p>
                  </div>
                </div>
                <Switch
                  checked={includeText}
                  onCheckedChange={setIncludeText}
                  data-testid="include-text-switch"
                />
              </div>

              <Button
                onClick={handleGenerate}
                disabled={loading || !title || !script}
                className="w-full h-12 bg-gradient-to-r from-[#00F0FF] to-[#FF0055] hover:opacity-90 text-white font-bold rounded-xl gap-2"
                data-testid="generate-thumbnail-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Thumbnail
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Style Preview Cards */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Style Guide
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {styles.map((s) => (
                  <div
                    key={s.value}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      style === s.value
                        ? "border-[#00F0FF] bg-[#00F0FF]/10"
                        : "border-border/30 hover:border-border/60"
                    }`}
                    onClick={() => setStyle(s.value)}
                  >
                    <p className="font-medium text-white text-sm">{s.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Preview Section */}
        <motion.div variants={itemVariants}>
          <Card className="bg-card border-border/50 h-full">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-white">
                Generated Thumbnail
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!generatedImage ? (
                <div className="aspect-[9/16] max-h-[500px] bg-secondary/30 rounded-xl flex items-center justify-center border-2 border-dashed border-border/50">
                  <div className="text-center p-8">
                    <Image className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Your AI-generated thumbnail will appear here
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Optimized for TikTok's 9:16 format
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="aspect-[9/16] max-h-[500px] bg-secondary/30 rounded-xl overflow-hidden border border-border/50">
                    <img
                      src={`data:image/png;base64,${generatedImage.image_data}`}
                      alt="Generated thumbnail"
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleDownload(generatedImage.image_data, title)}
                      className="flex-1 bg-[#00FF94] hover:bg-[#00FF94]/90 text-[#0A0A0B] font-bold rounded-xl gap-2"
                      data-testid="download-thumbnail-btn"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </Button>
                    <Button
                      onClick={handleRegenerate}
                      disabled={loading}
                      variant="outline"
                      className="flex-1 border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-xl gap-2"
                      data-testid="regenerate-thumbnail-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Regenerate
                    </Button>
                  </div>

                  {/* Info */}
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">
                      <span className="text-white font-medium">Style:</span> {generatedImage.style}
                      {generatedImage.id && (
                        <span className="ml-3">
                          <span className="text-[#00FF94]">✓</span> Saved to history
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Tips */}
      <motion.div variants={itemVariants}>
        <Card className="bg-gradient-to-r from-[#00F0FF]/10 to-[#FF0055]/10 border-border/50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#00F0FF]" />
              Thumbnail Tips for Higher CTR
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <span className="text-[#00F0FF]">•</span>
                Use high contrast colors that pop on mobile screens
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#FF0055]">•</span>
                Include expressive faces or emotions when relevant
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#00FF94]">•</span>
                Keep text large and readable - 3-5 words max
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
